(() => {
  'use strict';

  const BUY_NOW_PENDING_KEY = 'glasses_buy_now_pending';
  const BUY_NOW_BACKUP_KEY = 'glasses_buy_now_cart_backup';

  function getStoreRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * Shopify.routes.root 是相对路径（如 / 或 /en/），不能作为 new URL() 的 base，
   * 否则会解析到错误 origin 路径，购物车与结账会话不一致（甚至 401）。
   * 与 theme.js 一致：直接拼接 `${root}cart/...`。
   */
  function resolveUrl(path) {
    const root = getStoreRoot();
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return normalizedRoot + String(path).replace(/^\//, '');
  }

  function collectBundledSectionsParam() {
    const sections = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections },
      })
    );
    return Array.from(sections).join(',');
  }

  function buildJsonAddBody(items, sectionsParam) {
    const payload = { items };
    if (sectionsParam) payload.sections = sectionsParam;
    return JSON.stringify(payload);
  }

  async function fetchCartJson() {
    const res = await fetch(resolveUrl('cart.js'));
    if (!res.ok) throw new Error('Failed to read cart');
    return res.json();
  }

  async function clearCartJson() {
    const res = await fetch(resolveUrl('cart/clear.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: '{}',
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error((data && (data.message || data.description)) || 'Cart clear failed');
    }
    return data;
  }

  async function postCartAddJson(pathAndQuery, items, sectionsParam) {
    const res = await fetch(resolveUrl(pathAndQuery), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: buildJsonAddBody(items, sectionsParam),
    });

    const data = await res.json().catch(() => null);
    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        data,
        message: (data && (data.message || data.description || data.error)) || res.statusText || 'Cart add failed',
      };
    }
    return { ok: true, status: res.status, data };
  }

  async function buildCartSnapshotForTheme(addResponseData, bundledSectionsParam) {
    const cartContent = await fetchCartJson();

    if (addResponseData && addResponseData.sections) {
      cartContent.sections = addResponseData.sections;
      return cartContent;
    }

    if (bundledSectionsParam) {
      cartContent.sections = {};
      const ids = bundledSectionsParam.split(',');
      for (const rawId of ids) {
        const secId = rawId.trim();
        if (!secId) continue;
        const secRes = await fetch(resolveUrl(`?section_id=${encodeURIComponent(secId)}`));
        cartContent.sections[secId] = await secRes.text();
      }
    }

    return cartContent;
  }

  function dispatchCartChangeVariantAdd(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          cart,
        },
      })
    );
  }

  function openDrawerFallback() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    const cartDrawer = document.getElementById('cart-drawer');
    if (cartDrawer && typeof cartDrawer.show === 'function') {
      cartDrawer.show();
    }
  }

  async function notifyThemeAfterVariantAdd(addResponseData, bundledSectionsParam, options = {}) {
    if (!options.openDrawer) return;
    const cart = await buildCartSnapshotForTheme(addResponseData, bundledSectionsParam);
    if (cart.sections && Object.keys(cart.sections).length > 0) {
      dispatchCartChangeVariantAdd(cart);
    } else {
      openDrawerFallback();
    }
  }

  function normalizeProperties(properties) {
    if (!properties || typeof properties !== 'object') return {};
    const out = {};
    Object.keys(properties).forEach((k) => {
      const v = properties[k];
      if (v !== null && v !== undefined && String(v) !== '') out[k] = v;
    });
    return out;
  }

  async function replayBackupIntoCart(backupCart) {
    await clearCartJson();
    const lines = (backupCart && backupCart.items) || [];
    for (const line of lines) {
      await postCartAddJson(
        'cart/add.js',
        [
          {
            id: line.variant_id,
            quantity: line.quantity,
            properties: normalizeProperties(line.properties),
          },
        ],
        ''
      );
    }
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  async function beginBuyNowCheckoutSession() {
    const snapshot = await fetchCartJson();
    sessionStorage.setItem(BUY_NOW_BACKUP_KEY, JSON.stringify(snapshot));
    sessionStorage.setItem(BUY_NOW_PENDING_KEY, '1');
    await clearCartJson();
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  async function restoreBuyNowSessionIfAny() {
    if (sessionStorage.getItem(BUY_NOW_PENDING_KEY) !== '1') return;
    const raw = sessionStorage.getItem(BUY_NOW_BACKUP_KEY);
    sessionStorage.removeItem(BUY_NOW_PENDING_KEY);
    sessionStorage.removeItem(BUY_NOW_BACKUP_KEY);
    if (!raw) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    try {
      const backup = JSON.parse(raw);
      await replayBackupIntoCart(backup);
    } catch (e) {
      console.warn('Buy now cart restore failed', e);
    }
  }

  window.glassesCartIntegration = {
    collectBundledSectionsParam,
    postCartAddJson,
    notifyThemeAfterVariantAdd,
    beginBuyNowCheckoutSession,
    restoreBuyNowSessionIfAny,
    buildCartSnapshotForTheme,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      restoreBuyNowSessionIfAny();
    });
  } else {
    restoreBuyNowSessionIfAny();
  }
})();
